import { useMemo } from "react";
import { DateTimeSelectionStep } from "../../../appointment/components/DateTimeSelectionStep/DateTimeSelectionStep";
import { useBulkGroupClassAvailability } from "../../hooks";
import { useAppContext } from "../../../../../AppContext";
import {
  transformDateAvailabilityMap,
  hasAnyAvailableSlots,
  findFirstAvailableDate,
  getSelectedSlot,
  getSelectedSlotByEventId,
  createDummyService,
  getInstructorDisplayName,
  createDummyEmployee,
  isWaitlistBooking,
} from "./groupClassDateTimeUtilities";

interface IGroupClassDateTimeSelectionStepProps {
  instructorId?: number;
  instructorName?: string;
  selectedClassName?: string;
  className?: string;
  selectedDate?: Date | null;
  selectedTime?: string | null;
  selectedEventId?: number | null;
  onDateSelect: (date: Date) => void;
  onTimeSelect: (
    time: string,
    eventId: number,
    actualInstructorName?: string,
    actualInstructorId?: number,
    durationMinutes?: number,
    isWaitlistBooking?: boolean,
    requirePrepayment?: boolean,
    prepaymentLink?: string | null,
    requireFormCompletion?: boolean,
    formCompletionLink?: string | null,
    allowOnlineReservations?: boolean,
    prepaymentProvider?: number,
    price?: number,
    isMultisportAvailable?: boolean,
  ) => void;
  isLoading?: boolean;
}

/**
 * Wrapper around DateTimeSelectionStep that fetches group class availability
 * and transforms it to work with the reused appointment wizard component.
 * All business logic is extracted to utilities for cleaner code.
 */
export const GroupClassDateTimeSelectionStep = ({
  instructorId,
  instructorName,
  selectedClassName,
  className,
  selectedDate,
  selectedTime,
  selectedEventId,
  onDateSelect,
  onTimeSelect,
  isLoading,
}: IGroupClassDateTimeSelectionStepProps) => {
  const { selectedStudio } = useAppContext();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Fetch bulk availability for group classes
  const {
    dateAvailabilityMap,
    hasAnySlots: hasAnySlotsForDate,
    getDateAvailability,
    isLoading: bulkLoading,
    hasData: bulkHasData,
    visibleDaysCount,
  } = useBulkGroupClassAvailability({
    instructorId,
    className: selectedClassName,
    startDate: today,
    studioId: selectedStudio?.id,
  });

  // Transform group class time slots to appointment format
  const transformedDateAvailabilityMap = useMemo(
    () => transformDateAvailabilityMap(dateAvailabilityMap),
    [dateAvailabilityMap]
  );

  // Handle time selection
  const handleTimeSelect = (time: string, eventId?: number) => {
    if (!selectedDate) return;

    const selectedSlot = eventId != null
      ? getSelectedSlotByEventId(dateAvailabilityMap, eventId)
      : getSelectedSlot(dateAvailabilityMap, selectedDate, time);
    if (!selectedSlot) return;

    onTimeSelect(
      time,
      selectedSlot.eventId,
      selectedSlot.instructorName,
      selectedSlot.instructorId,
      selectedSlot.durationMinutes,
      isWaitlistBooking(selectedSlot),
      selectedSlot.requirePrepayment,
      selectedSlot.prepaymentLink,
      selectedSlot.requireFormCompletion,
      selectedSlot.formCompletionLink,
      selectedSlot.allowOnlineReservations,
      selectedSlot.prepaymentProvider,
      selectedSlot.price,
      selectedSlot.isMultisportAvailable,
    );
  };

  // Check if any slots are available
  const hasAnySlots = () => hasAnyAvailableSlots(dateAvailabilityMap);

  // Jump to first available date
  const jumpToFirstAvailable = () => {
    const firstAvailableDate = findFirstAvailableDate(dateAvailabilityMap);
    if (firstAvailableDate) {
      onDateSelect(firstAvailableDate);
      return true;
    }
    return false;
  };

  // Get date availability in appointment format
  const getAppointmentDateAvailability = (date: Date) => {
    const dateKey = date.toISOString().split("T")[0];
    return transformedDateAvailabilityMap.get(dateKey);
  };

  // Create dummy service/employee objects for compatibility
  const dummyService = createDummyService(selectedClassName);

  const dummyEmployee = useMemo(() => {
    if (!instructorId) return null;

    const displayName = getInstructorDisplayName(
      instructorId,
      instructorName,
      selectedDate,
      selectedTime,
      dateAvailabilityMap,
      selectedEventId,
    );

    return createDummyEmployee(instructorId, displayName);
  }, [instructorId, instructorName, selectedTime, selectedDate, dateAvailabilityMap, selectedEventId]);

  return (
    <DateTimeSelectionStep
      className={className}
      selectedService={dummyService}
      selectedEmployee={dummyEmployee}
      selectedDate={selectedDate}
      selectedTime={selectedTime}
      selectedEventId={selectedEventId}
      onDateSelect={onDateSelect}
      onTimeSelect={handleTimeSelect}
      onJumpToFirstAvailable={jumpToFirstAvailable}
      hasAnyAvailableSlots={hasAnySlots}
      isLoading={isLoading}
      // Pass pre-fetched availability data to prevent internal hook call
      dateAvailabilityMap={transformedDateAvailabilityMap}
      hasAvailableSlots={hasAnySlotsForDate}
      getDateAvailability={getAppointmentDateAvailability}
      bulkAvailabilityLoading={bulkLoading}
      bulkAvailabilityHasData={bulkHasData}
      bulkAvailabilityVisibleDaysCount={visibleDaysCount}
      // Custom labels for group class mode
      serviceLabelText="Клас:"
      employeeLabelText="Инструктор:"
    />
  );
};
